import type { Address } from '../../types/address';
import { ADDRESS_FRAGMENT } from '../../types/address';
import type { Car } from '../../types/car';
import { CAR_FRAGMENT } from '../../types/car';
import type { Driver } from '../../types/driver';
import { DRIVER_FRAGMENT } from '../../types/driver';
import type { Offer } from '../../types/offer';
import type { ReferralSource } from '../../types/referral_source.enum';

export interface User {
  id: string;
  name: string;
  phone: string;
  phoneVerified: boolean;
  email: string | null;
  mlCoverageEnabled: boolean;
  isSigned: boolean;
  signupParams: string | null;
  isHomeowner: boolean | null;
  telematics: boolean | null;
  dateOfBirthMasked: { value: string } | null;
  age: number | null;
  address: Address | null;
  driverID: string | null;
  referralSource: ReferralSource | null;
  isOnboardingLite: boolean;
  isBeyondDrivingAge: boolean;
  isFromMAL?: boolean;
  cars: {
    edges: { node: Car }[];
  } | null;
  drivers: {
    edges: { node: Driver }[];
  } | null;
  refiOffers?: Offer[];
  refiIsCheckingOffer?: boolean;
  enableLDU?: boolean;
  averageCarPolicyPriceByState?: number | null;
  fieldsUpdatedAt?: Record<string, string>;
}

export const defaultUserData: User = {
  id: '',
  name: '',
  phone: '',
  email: null,
  phoneVerified: false,
  mlCoverageEnabled: false,
  isSigned: false,
  signupParams: null,
  isHomeowner: null,
  telematics: null,
  dateOfBirthMasked: null,
  age: null,
  address: null,
  driverID: null,
  referralSource: null,
  cars: null,
  drivers: null,
  isOnboardingLite: false,
  isBeyondDrivingAge: true,
  enableLDU: false,
};

export const BASE_USER_FRAGMENT = `
  id
  name
  phone
  phoneVerified
  email
  mlCoverageEnabled
  isSigned
  signupParams
  isHomeowner
  telematics
  dateOfBirthMasked {
    value
  }
  age
  referralSource
  isOnboardingLite
  isBeyondDrivingAge
  address {
    ${ADDRESS_FRAGMENT}
  }
  driverID
  cars {
    edges {
      node {
        ${CAR_FRAGMENT}
      }
    }
  }
  drivers {
    edges {
      node {
        ${DRIVER_FRAGMENT}
      }
    }
  }
  enableLDU
  averageCarPolicyPriceByState
  fieldsUpdatedAt
`;

export const USER_FRAGMENT = `
  isFromMAL
  ${BASE_USER_FRAGMENT}
`;

const POLICY_FRAGMENT = `
  id
  type
  term
  tier
  premium
  carrierName
  carrierID
  carIDs
  driverIDs
  additionalDriverIDs
  carrier {
    id
    name
    nickname
    ratingValue
    ratingReviews
  }
  coverage {
    edges {
      node {
        id
        assetID
        label
        ciName
        ciValue
      }
    }
  }
  quotePaymentPlans {
    id
    term
    premium
    downPayment
    monthlyPayment
    monthlyPaymentCount
    paymentType
    paymentMethod
  }
  averagePriceByCarrier
  averagePriceByState
  userAddressSnapshot
  buyMetadata
`;

export const USER_QUOTES_FRAGMENT = `
  id
  stage
  isQuotingInProgress
  currentQuotingProgress
  currentBatchFailedReasons
  currentBatchFinishedTiers
  currentBatchCheckedCarriersCount
  activePolicies {
    ${POLICY_FRAGMENT}
  }
  bundledQuotes(needTelematicsPrices: true) {
    id
    term
    savings
    policyEdges {
      premium
      policy {
        ${POLICY_FRAGMENT}
      }
    }
  }
`;

export const BASE_USER_AND_QUOTE_FRAGMENT = `
  id
  name
  phone
  phoneVerified
  mlCoverageEnabled
  isSigned
  signupParams
  isHomeowner
  telematics
  dateOfBirthMasked {
    value
  }
  age
  referralSource
  isOnboardingLite
  isBeyondDrivingAge
  address {
    ${ADDRESS_FRAGMENT}
  }
  driverID
  cars {
    edges {
      node {
        ${CAR_FRAGMENT}
      }
    }
  }
  drivers {
    edges {
      node {
        ${DRIVER_FRAGMENT}
      }
    }
  }
  isFromMAL
  enableLDU
  stage
  isQuotingInProgress
  currentQuotingProgress
  currentBatchFailedReasons
  currentBatchFinishedTiers
  currentBatchCheckedCarriersCount
  activePolicies {
    ${POLICY_FRAGMENT}
  }
  bundledQuotes(needTelematicsPrices: true) {
    id
    term
    savings
    policyEdges {
      premium
      policy {
        ${POLICY_FRAGMENT}
      }
    }
  }
  averageCarPolicyPriceByState
`;
